import { FC, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import Image from './Image';
import { LightboxItem, LightboxWrapper } from './LightboxModal';
import { useTranslation } from 'react-i18next';
import useFetchDocument from '../hooks/useFetchDocument';
import { AppContext } from '../contexts/AppContext';
import { getTranslation } from '../common/Translation';
import { getPollData, submitPollData } from '../services/Api';
import BlockContainer from './BlockContainer';

interface BlockPollProps {
  pollData?: any[];
}

interface PollItemProps {
  pollItem: any;
}

interface PollApiResponse {
  updated_at: string;
  created_at: string;
  document_id: number;
  show_result: boolean;
  poll_data: Record<string, number>;
}

const BlockContain = styled(BlockContainer)`
  display: block;
`;

const Paper = styled.div`
  width: 100%;
`;

const TaskContainer = styled.div`
  border-radius: 8px;
  overflow: hidden;
  margin: 0 auto;
  display: flex;
  align-items: stretch;
  background-color: ${p => p.theme.blockColor};
  justify-content: center;
`;

const TaskWrapper = styled.div`
  position: relative;
  padding: 4vh 8vh;
  z-index: 10;
  border-radius: 4px;
  color: #fff;
  flex-grow: 1;
  flex-shrink: 3;
  min-height: 20vw;
  display: flex;
  flex-direction: column;
  justify-content: center;
  max-width: 57vw;

  @media (max-width: 1200px) {
    padding: 2vh 4vh;
    width: 55vw;
  }
`;

const ImageWrapper = styled.div`
  width: 22vw;

  & div {
    height: 100%;
  }

  @media (max-width: 1200px) {
    width: 25vw;
    overflow-x: hidden;
  }
`;

const HeroImage = styled(Image)`
  height: 100%;
  aspect-ratio: 1;
  overflow: hidden;
  max-width: 22vw;

  @media (max-width: 1200px) {
    max-width: 25vw;
  }

  /* iOS 14 has no aspect-ratio, so fall back to the max width */
  @supports not (aspect-ratio: 1) {
    width: 22vw;

    @media (max-width: 1200px) {
      width: 25vw;
    }
  }
`;

const Question = styled.span`
  font-size: 1.2em;
  font-weight: bold;
  display: block;
  margin-bottom: 35px;
`;

const Answers = styled.div`
  margin: 10px 0 0 0;
  padding: 0;
  position: relative;
`;

const Answer = styled.button`
  width: 100%;
  padding: 15px 35px;
  border: 2px solid #C7D0D9;
  background-color: transparent;
  border-radius: 32px;
  display: flex;
  justify-content: center;
  align-items: center;
  font-size: 1em;
  cursor: pointer;
  color: #fff;

  &:not(:last-child) {
    margin-bottom: 20px;
  }

  &:disabled {
    cursor: default;
    opacity: 0.6;
  }

  &:active {
    background-color: ${p => p.theme.accentColor}30;
  }
`;

const AnswerText = styled.span`
  pointer-events: none;
  color: #fff;
  text-align: center;
`;

const Votes = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  position: relative;
`;

const Vote = styled.li`
  padding-bottom: 22px;
`;

const VoteAnswer = styled.span`
  max-width: calc(100% - 45px);
  display: block;
  margin-bottom: 12px;
  float: left;
`;

const VotePercentage = styled.span`
  float: right;
  margin-bottom: 10px;
`;

const VoteGraph = styled.div`
  width: 100%;
  height: 22px;
  border-radius: 12px;
  clear: both;
  background: #DBE2EB;
  overflow: hidden;
`;

const VoteGraphInner = styled.div<{ $percentage?: number }>`
  width: 100%;
  height: 22px;
  background-color: ${p => p.theme.accentColor};
  transform-origin: left;
  transform: scaleX(${p => (p.$percentage || 0) / 100});
  transition: transform 0.8s ease;
`;

const VoteCount = styled.p`
  margin: 10px 0 0;
`;

const SeeResult = styled.button`
  background: none;
  border: none;
  padding: 0;
  color: ${p => p.theme.accentColor};
  cursor: pointer;
  font: inherit;
  margin-top: 10px;

  &:disabled {
    cursor: default;
    opacity: 0.6;
  }

  &:focus-visible {
    outline: 2px solid ${p => p.theme.accentColor};
    outline-offset: 2px;
  }
`;

/**
 * Renders all poll blocks.
 */
const BlockPoll: FC<BlockPollProps> = ({ pollData }) => {
  if (!pollData?.length) return null;

  return (
    <>
      {pollData.map((item: any, i: number) => (
        <PollItem pollItem={item} key={`poll${item.to_document_id || i}`} />
      ))}
    </>
  );
};

/**
 * Renders a single poll.
 *
 * Handles:
 * - fetching poll document
 * - submitting vote
 * - fetching poll results
 * - calculating percentages
 * - showing answer buttons or result view
 */
const PollItem: FC<PollItemProps> = ({ pollItem }) => {
  const { t } = useTranslation();
  const { currentLanguage, previewIsActive, answeredPolls, setAnsweredPolls } = useContext(AppContext);
  const [currentMedia, setCurrentMedia] = useState<any>(null);
  const [isAnswered, setIsAnswered] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [results, setResults] = useState<Record<string, number>>({});
  const [pollData] = useFetchDocument(pollItem.to_document_id, pollItem.previewData);

  /**
   * Total number of submitted votes.
   */
  const totalVotes = useMemo(() => {
    return Object.values(results).reduce((sum, count) => {
      return sum + Number(count || 0);
    }, 0);
  }, [results]);

  /**
   * Fetches poll result data from API.
   */
  const fetchResults = useCallback(async () => {
    if (!pollItem.to_document_id) return;
    const data = await getPollData(pollItem.to_document_id) as PollApiResponse | false | undefined;

    setResults(data && data.poll_data ? data.poll_data : {});
  }, [pollItem.to_document_id]);

  /**
   * Calculates vote percentage for a given answerId.
   */
  const getPercentage = useCallback((answerId: string): number => {
    const count = Number(results?.[answerId] || 0);

    if (!totalVotes) return 0;

    return Math.round((count / totalVotes) * 100);
  }, [results, totalVotes]);

  /**
   * Submits a vote to the API.
   *
   * Uses answer.answerId as response_key.
   */
  const processAnswer = useCallback(async (answer: any) => {
    if (isLoading || !pollItem.to_document_id || !answer?.answerId) return;
    setIsLoading(true);
    await submitPollData(pollItem.to_document_id, answer.answerId);
    setIsAnswered(true);
    await fetchResults();
    setIsLoading(false);

    setAnsweredPolls(prev => {
      if (prev.includes(pollItem.to_document_id)) { return prev; }
      return [...prev, pollItem.to_document_id];
    });
  }, [
    isLoading,
    pollItem.to_document_id,
    fetchResults,
    setAnsweredPolls
  ]);

  /**
   * Shows results without submitting a vote.
   */
  const showResults = useCallback(async () => {
    if (isLoading) return;

    setIsAnswered(true);
    setIsLoading(true);

    await fetchResults();

    setIsLoading(false);
  }, [isLoading, fetchResults]);

  // Check if poll already is answered
  useEffect(() => {
    if (isLoading) return;

    if (answeredPolls?.includes(pollItem.to_document_id)) {
      setIsAnswered(true);
      showResults();
    } else if (!isAnswered) {
      setIsAnswered(false);
    }
  }, [answeredPolls, pollItem.to_document_id, isAnswered, showResults, isLoading]);

  if (pollData?.status !== 'published' && !previewIsActive) return null;

  return (
    <BlockContain className="showInPreview">
      <Paper>
        <TaskContainer>
          {pollData?.content?.general?.image?.mediaId && (
            <LightboxWrapper>
              <LightboxItem media={currentMedia}>
                <ImageWrapper>
                  <HeroImage
                    media={pollData?.content?.general?.image}
                    setMedia={setCurrentMedia}
                  />
                </ImageWrapper>
              </LightboxItem>
            </LightboxWrapper>
          )}

          <TaskWrapper className={pollData?.media?.[0]?.id ? 'withImage' : ''}>
            <Question>
              {getTranslation(
                pollData?.content?.general?.question,
                currentLanguage
              )}
            </Question>

            {!isAnswered ? (
              <Answers>
                {pollData?.content?.answers?.map((answer: any, i: number) => {
                  const answerText = getTranslation(answer.answer, currentLanguage);

                  if (!answerText?.length) return null;

                  return (
                    <Answer
                      key={`answer${answer.answerId || i}`}
                      disabled={isLoading}
                      onClick={() => processAnswer(answer)}
                    >
                      <AnswerText>{answerText}</AnswerText>
                    </Answer>
                  );
                })}

                {pollData?.content?.settings?.showResultsWithoutVoting &&
                  pollData?.content?.answers?.length > 0 && (
                    <SeeResult onClick={showResults} disabled={isLoading}>
                      {t('votes.seeResult')}
                    </SeeResult>
                  )}
              </Answers>
            ) : (
              <Votes>
                {pollData?.content?.answers?.map((answer: any, i: number) => {
                  const answerText = getTranslation(answer.answer, currentLanguage);

                  if (!answerText?.length) return null;

                  const percentage = getPercentage(answer.answerId);

                  return (
                    <Vote key={`result${answer.answerId || i}`}>
                      <VoteAnswer>{answerText}</VoteAnswer>
                      <VotePercentage>{percentage}%</VotePercentage>
                      <VoteGraph>
                        <VoteGraphInner $percentage={percentage} />
                      </VoteGraph>
                    </Vote>
                  );
                })}

                <VoteCount>
                  {t('votes.total', { count: totalVotes })}
                </VoteCount>
              </Votes>
            )}
          </TaskWrapper>
        </TaskContainer>
      </Paper>
    </BlockContain>
  );
};

export default BlockPoll;